import { Component, inject } from '@angular/core';
import { LucideAngularModule, ArrowRight } from 'lucide-angular';
import { ModalService } from '../services/modal.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-hero',
  standalone: true,
  imports: [LucideAngularModule, TranslateModule],
  template: `
    <section class="relative min-h-screen flex items-center overflow-hidden bg-dark-100">
      <!-- Subtle warm glow -->
      <div class="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-primary/[0.06] rounded-full blur-[100px] pointer-events-none"></div>

      <div class="max-w-7xl mx-auto px-5 md:px-10 pt-28 pb-16 w-full relative z-10">
        <div class="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

          <!-- Left: Text content -->
          <div class="flex-1 max-w-2xl" data-aos="fade-up">
            <p class="text-primary font-bold text-xs tracking-[0.2em] uppercase mb-5">{{ 'HERO.LABEL' | translate }}</p>
            
            <h1 class="text-[2.75rem] sm:text-5xl md:text-[4.5rem] font-black leading-[1] tracking-tight mb-6">
              {{ 'HERO.TITLE_1' | translate }}<br/>
              <span class="text-gradient">{{ 'HERO.TITLE_2' | translate }}</span>
            </h1>
            
            <p class="text-lg md:text-xl text-gray-450 max-w-xl mb-10 leading-relaxed">
              {{ 'HERO.DESC' | translate }}
            </p>

            <div class="flex flex-col sm:flex-row items-start gap-3 mb-16">
              <button (click)="modalService.openComingSoon($event)" class="flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-white font-bold text-sm transition-all hover:bg-primary-dark shadow-cta hover:shadow-cta-hover">
                {{ 'HERO.START_BTN' | translate }}
                <lucide-icon [img]="ArrowRight" class="w-4 h-4"></lucide-icon>
              </button>
              <button (click)="modalService.openComingSoon($event)" class="px-7 py-3.5 rounded-full border border-gray-750 text-white font-bold text-sm hover:border-gray-550 hover:bg-white/[0.03] transition-all">
                {{ 'HERO.LEARN_BTN' | translate }}
              </button>
            </div>
          </div>

          <!-- Right: Distribution flow visual -->
          <div class="relative w-full max-w-[360px] md:max-w-[400px] h-[440px] md:h-[480px] flex-shrink-0 mx-auto" data-aos="fade-left" data-aos-delay="300">

            <!-- Flow lines SVG — behind everything -->
            <svg class="absolute inset-0 w-full h-full pointer-events-none z-10" viewBox="0 0 400 480" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="flowGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stop-color="#FF5620" stop-opacity="0.6"/>
                  <stop offset="100%" stop-color="#FF5620" stop-opacity="0.05"/>
                </linearGradient>
              </defs>
              <!-- Row 1: Colorfol (200,75) → Spotify (75,200) -->
              <path d="M 200 75 C 200 130, 75 140, 75 195" stroke="url(#flowGrad)" stroke-width="1.5" fill="none" class="flow-line"/>
              <!-- Row 1: Colorfol (200,75) → Apple Music (200,190) -->
              <path d="M 200 75 C 200 120, 200 150, 200 190" stroke="url(#flowGrad)" stroke-width="1.5" fill="none" class="flow-line flow-delay-1"/>
              <!-- Row 1: Colorfol (200,75) → YouTube (325,200) -->
              <path d="M 200 75 C 200 130, 325 140, 325 195" stroke="url(#flowGrad)" stroke-width="1.5" fill="none" class="flow-line flow-delay-2"/>
              <!-- Row 2: Colorfol (200,75) → Deezer (65,345) -->
              <path d="M 200 75 C 180 180, 65 260, 65 340" stroke="url(#flowGrad)" stroke-width="1" fill="none" class="flow-line flow-delay-3"/>
              <!-- Row 2: Colorfol (200,75) → Boomplay (165,350) -->
              <path d="M 200 75 C 195 180, 165 270, 165 345" stroke="url(#flowGrad)" stroke-width="1" fill="none" class="flow-line flow-delay-1"/>
              <!-- Row 2: Colorfol (200,75) → Audiomack (270,348) -->
              <path d="M 200 75 C 210 180, 270 270, 270 343" stroke="url(#flowGrad)" stroke-width="1" fill="none" class="flow-line flow-delay-2"/>
              <!-- Row 2: Colorfol (200,75) → TikTok (345,345) -->
              <path d="M 200 75 C 220 180, 345 260, 345 340" stroke="url(#flowGrad)" stroke-width="1" fill="none" class="flow-line flow-delay-3"/>
            </svg>

            <!-- Colorfol source card — TOP, prominent -->
            <div class="absolute top-0 left-1/2 -translate-x-1/2 z-30">
              <div class="relative">
                <div class="absolute -inset-3 bg-primary/20 rounded-3xl blur-[25px] animate-pulse-slow"></div>
                <div class="relative flex items-center gap-3 bg-dark-200/90 backdrop-blur-xl border border-primary/40 rounded-2xl px-5 py-3.5 shadow-2xl shadow-primary/10 whitespace-nowrap">
                  <div class="w-11 h-11 rounded-xl bg-white flex items-center justify-center p-2 flex-shrink-0">
                    <img src="other/icon/colorfol_logo.png" alt="Colorfol" class="w-full h-full object-contain" />
                  </div>
                  <div>
                    <span class="text-sm font-black text-white block leading-tight">Colorfol</span>
                    <span class="text-[10px] text-primary font-bold whitespace-nowrap">{{ 'HERO.SOURCE_TEXT' | translate }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ROW 1: Big 3 platforms -->
            <div class="absolute top-[185px] left-0 sm:left-[20px] animate-float-slow z-20">
              <div class="platform-card w-[85px] sm:w-[100px]">
                <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white p-2 mx-auto mb-1.5">
                  <img src="other/icon/Spotify_logo_without_text.svg.png" alt="Spotify" class="w-full h-full object-contain" />
                </div>
                <span class="text-[9px] font-bold text-gray-400 block text-center">Spotify</span>
              </div>
            </div>

            <div class="absolute top-[180px] left-1/2 -translate-x-1/2 animate-float-delayed z-20">
              <div class="platform-card w-[85px] sm:w-[100px]">
                <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white p-2 mx-auto mb-1.5">
                  <img src="other/icon/Apple_Music_icon.svg.png" alt="Apple Music" class="w-full h-full object-contain" />
                </div>
                <span class="text-[9px] font-bold text-gray-400 block text-center">Apple Music</span>
              </div>
            </div>

            <div class="absolute top-[185px] right-0 sm:right-[20px] animate-float z-20">
              <div class="platform-card w-[85px] sm:w-[100px]">
                <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white p-2 mx-auto mb-1.5">
                  <img src="other/icon/Youtube_Music_icon.svg.png" alt="YouTube Music" class="w-full h-full object-contain" />
                </div>
                <span class="text-[9px] font-bold text-gray-400 block text-center">YouTube</span>
              </div>
            </div>

            <!-- ROW 2: Smaller platforms -->
            <div class="absolute top-[330px] left-0 sm:left-[10px] animate-float-delayed z-20">
              <div class="platform-card-sm w-[70px] sm:w-[78px]">
                <div class="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-white p-1 sm:p-1.5 mx-auto mb-1">
                  <img src="other/icon/DEEZER.PA-dbdcf2cf.png" alt="Deezer" class="w-full h-full object-contain" />
                </div>
                <span class="text-[8px] font-bold text-gray-500 block text-center">Deezer</span>
              </div>
            </div>

            <div class="absolute top-[335px] left-[85px] sm:left-[115px] animate-float-slow z-20">
              <div class="platform-card-sm w-[70px] sm:w-[78px]">
                <div class="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-white p-1 sm:p-1.5 mx-auto mb-1">
                  <img src="other/icon/BOOMPLAY.png" alt="Boomplay" class="w-full h-full object-contain" />
                </div>
                <span class="text-[8px] font-bold text-gray-500 block text-center">Boomplay</span>
              </div>
            </div>

            <div class="absolute top-[333px] right-[85px] sm:right-[95px] animate-float z-20">
              <div class="platform-card-sm w-[70px] sm:w-[78px]">
                <div class="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-white p-1 sm:p-1.5 mx-auto mb-1">
                  <img src="other/icon/audiomack.png" alt="Audiomack" class="w-full h-full object-contain" />
                </div>
                <span class="text-[8px] font-bold text-gray-500 block text-center">Audiomack</span>
              </div>
            </div>

            <div class="absolute top-[330px] right-0 sm:right-[10px] animate-float-delayed z-20">
              <div class="platform-card-sm w-[70px] sm:w-[78px]">
                <div class="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-white p-1 sm:p-1.5 mx-auto mb-1">
                  <img src="other/icon/tiktok-6338432_1280.png" alt="TikTok" class="w-full h-full object-contain" />
                </div>
                <span class="text-[8px] font-bold text-gray-500 block text-center">TikTok</span>
              </div>
            </div>



          </div>

        </div>

        <!-- Platform logos strip -->
        <div class="border-t border-white/[0.06] pt-8 mt-4" data-aos="fade-up" data-aos-delay="200">
          <p class="text-[11px] font-bold tracking-[0.2em] uppercase text-gray-650 mb-5">{{ 'HERO.STRIP_TITLE' | translate }}</p>
          <div class="flex flex-wrap items-center gap-x-7 gap-y-5">
            <img src="other/Spotify_logo_with_text.svg.png" alt="Spotify" class="h-5 opacity-50 hover:opacity-90 transition-opacity brightness-0 invert" />
            <img src="other/apple_music_logo.png" alt="Apple Music" class="h-5 opacity-50 hover:opacity-90 transition-opacity brightness-0 invert" />
            <img src="other/YouTube_Music_2024.svg" alt="YouTube Music" class="h-5 opacity-50 hover:opacity-90 transition-opacity brightness-0 invert" />
            <img src="other/Logo_Deezer_2023.svg" alt="Deezer" class="h-5 opacity-50 hover:opacity-90 transition-opacity brightness-0 invert" />
            <img src="other/TikTok_logo.svg.png" alt="TikTok" class="h-5 opacity-50 hover:opacity-90 transition-opacity brightness-0 invert" />
            <img src="other/boomplay-new-logo-1.svg" alt="Boomplay" class="h-5 opacity-50 hover:opacity-90 transition-opacity brightness-0 invert" />
            <img src="other/audiomack_logo.png" alt="Audiomack" class="h-5 opacity-50 hover:opacity-90 transition-opacity brightness-0 invert" />
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }
    @keyframes float-slow {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-7px); }
    }
    @keyframes float-delayed {
      0%, 100% { transform: translateY(2px); }
      50% { transform: translateY(-8px); }
    }
    @keyframes pulse-slow {
      0%, 100% { opacity: 0.5; }
      50% { opacity: 1; }
    }
    @keyframes dash-flow {
      0% { stroke-dashoffset: 20; }
      100% { stroke-dashoffset: 0; }
    }

    :host ::ng-deep .animate-float { animation: float 4s ease-in-out infinite; }
    :host ::ng-deep .animate-float-slow { animation: float-slow 5s ease-in-out infinite; }
    :host ::ng-deep .animate-float-delayed { animation: float-delayed 6s ease-in-out infinite; }
    :host ::ng-deep .animate-pulse-slow { animation: pulse-slow 3s ease-in-out infinite; }

    .flow-line {
      stroke-dasharray: 6 4;
      animation: dash-flow 1.5s linear infinite;
    }
    .flow-delay-1 { animation-delay: 0.3s; }
    .flow-delay-2 { animation-delay: 0.6s; }
    .flow-delay-3 { animation-delay: 0.9s; }

    .platform-card {
      background: rgba(255,255,255,0.03);
      backdrop-filter: blur(12px);
      border: 1px solid rgba(255,255,255,0.06);
      border-radius: 16px;
      padding: 12px 8px 10px;
      transition: all 0.3s ease;
    }
    .platform-card:hover {
      border-color: rgba(255,255,255,0.15);
      background: rgba(255,255,255,0.06);
      transform: translateY(-2px);
    }
    .platform-card-sm {
      background: rgba(255,255,255,0.02);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255,255,255,0.04);
      border-radius: 12px;
      padding: 8px 6px 6px;
      transition: all 0.3s ease;
    }
    .platform-card-sm:hover {
      border-color: rgba(255,255,255,0.12);
      background: rgba(255,255,255,0.05);
    }
  `]
})
export class HeroComponent {
  modalService = inject(ModalService);
  readonly ArrowRight = ArrowRight;
}
